import store from "@/store"
import ActionId from "@/action/ActionId"
import { sessionState } from "@/state/index"

function getResponse(type, providerKey = null) {
    providerKey = providerKey || sessionState.providerKey
    return store.state.provider.responses[providerKey]?.[type]
}

function getAllResponses(type) {
    return Object.keys(store.state.provider.responses)
        .map(providerKey => getResponse(type, providerKey))
        .filter(response => !!response)
}

function setResponse(type, providerKey, response) {
    return store.commit("provider/saveResponse", {
        type,
        providerKey,
        response,
    })
}

const applyType = "apply"
const preAuthType = "preAuth"
const prescreenType = "prescreen"
const paymentPlansType = "paymentPlans"
const stipulationsType = "stipulations"

export default {
    get responses() {
        return store.state.provider.responses || {}
    },

    get primaryProvider() {
        return store.state.provider.primaryProvider
    },

    set primaryProvider(provider) {
        store.commit("provider/updatePrimaryProvider", provider)
    },

    get providers() {
        return store.state.provider.providers
    },

    getProviderProperties(providerKey) {
        return store.state.provider.properties[providerKey]
    },

    getAllProviderProperties() {
        return store.state.provider.properties
    },

    setProviderProperties(providerKey, properties) {
        store.commit("provider/updateProviderProperties", { providerKey, properties })
    },

    get excludedProviders() {
        return store.state.provider.excludedProviders
    },

    set providers(providers) {
        store.commit("provider/updateProviders", providers)
    },

    get applyResponse() {
        return getResponse(applyType)
    },

    getApplyResponse(providerKey) {
        return getResponse(applyType, providerKey)
    },

    setApplyResponse(providerKey, response) {
        return setResponse(applyType, providerKey, response)
    },

    getPaymentPlans(providerKey = null) {
        return getResponse(paymentPlansType, providerKey) || []
    },

    getAllPaymentPlans() {
        const allPaymentPlans = getAllResponses(preAuthType).flatMap(response => {
            return getResponse(paymentPlansType, response.providerKey) || []
        })
        return allPaymentPlans
    },

    setPaymentPlans(providerKey, paymentPlans) {
        store.commit("provider/updatePaymentPlans", {
            providerKey,
            paymentPlans,
        })
    },

    set excludedProviders(providerKeys) {
        store.commit("provider/setExcludedProviders", {
            providerKeys,
        })
    },

    getStipulations(providerKey = null) {
        return getResponse(stipulationsType, providerKey) || []
    },

    setStipulations(providerKey, stipulations) {
        store.commit("provider/updateStipulations", {
            providerKey,
            stipulations,
        })
    },

    updateStipulation(providerKey, stipulation) {
        store.commit("provider/updateStipulation", {
            providerKey,
            stipulation,
        })
    },

    getError(providerKey, errorType) {
        providerKey = providerKey || sessionState.providerKey
        return store.state.provider.errors[providerKey]?.[errorType]
    },

    get preAuthResponse() {
        return getResponse(preAuthType)
    },

    getPreAuthResponse(providerKey = null) {
        return getResponse(preAuthType, providerKey)
    },

    getAllPreAuthResponses() {
        return getAllResponses(preAuthType)
    },

    getAllApplyResponses() {
        return getAllResponses(applyType)
    },

    setPreAuthResponse(providerKey, response) {
        return setResponse(preAuthType, providerKey, response)
    },

    getPrescreenResponse(providerKey) {
        return getResponse(prescreenType, providerKey)
    },

    setPrescreenResponse(providerKey, response) {
        return setResponse(prescreenType, providerKey, response)
    },

    addError(providerKey, errorType, error) {
        return store.commit("provider/addError", {
            providerKey,
            errorType,
            error,
        })
    },

    removeError(providerKey, errorType) {
        return store.commit("provider/removeError", {
            providerKey,
            errorType,
        })
    },

    saveActionResponse({ id, providerKey }, response) {
        switch (id) {
            case ActionId.SUBMIT_PREQUALIFLY:
            case ActionId.SUBMIT_PRESCREEN:
                return this.setPreAuthResponse(providerKey, response)
            case ActionId.ACCEPT_OFFER:
            case ActionId.SUBMIT_FULL_APPlY:
                return this.setApplyResponse(providerKey, response)
            default:
                console.warn("Not saving provider response because of unsupported action", id)
        }
    },
}
